import { ChevronDown, ChevronRight, Eye, Plus } from 'lucide-react';
import type { SkillTreeNodeData } from './skill-framework-chart-utils';
import { normalizeSkillDescription } from './skill-framework-chart-utils';
import SkillFrameworkActions from './skill-framework-hierarchy-actions';

interface SkillFrameworkHierarchyDrawerProps {
    treeData: SkillTreeNodeData | null;
    nodeStack: SkillTreeNodeData[];
    getSkillTypeLabel: (id?: number | null) => string;
    collapsedMap: Record<string, boolean>;
    onToggle: (nodeId: string) => void;
    onView: (nodeId: string) => void;
    isStudent: boolean;
    canManage: boolean;
    height: number | string;
    isPublished: boolean;
    isPublishing: boolean;
    onPublish: () => void;
    onCreateChild: (nodeId: string) => void;
    onEdit: (nodeId: string) => void;
    isDuplicating: boolean;
    onDuplicate: (nodeId: string) => void;
    isDeleting: boolean;
    onDelete: (nodeId: string) => void;
    rootId: string | number;
}

interface HierarchyNodeItemProps {
    node: SkillTreeNodeData;
    depth: number;
    isRoot: boolean;
    getSkillTypeLabel: (id?: number | null) => string;
    collapsedMap: Record<string, boolean>;
    onToggle: (nodeId: string) => void;
    onView: (nodeId: string) => void;
    isStudent: boolean;
    canManage: boolean;
    isPublished: boolean;
    isPublishing: boolean;
    onPublish: () => void;
    onCreateChild: (nodeId: string) => void;
    onEdit: (nodeId: string) => void;
    isDuplicating: boolean;
    onDuplicate: (nodeId: string) => void;
    isDeleting: boolean;
    onDelete: (nodeId: string) => void;
}

const HierarchyNodeItem = ({
    node,
    depth,
    isRoot,
    collapsedMap,
    onToggle,
    onView,
    isStudent,
    canManage,
    getSkillTypeLabel,
    isPublished,
    isPublishing,
    onPublish,
    onCreateChild,
    onEdit,
    isDuplicating,
    onDuplicate,
    isDeleting,
    onDelete,
}: HierarchyNodeItemProps) => {
    const hasChildren = !!node.children?.length;
    const isCollapsed = collapsedMap[node.id] ?? false;
    const showActionsAlways = isRoot;
    const showView = isStudent || canManage;

    return (
        <div>
            <div
                className="group flex items-center justify-between gap-2 py-1.5"
                style={{ paddingLeft: `${depth * 16}px` }}
            >
                <div className="flex items-start gap-2 min-w-0">
                    {hasChildren ? (
                        <button
                            type="button"
                            onClick={() => onToggle(node.id)}
                            className="mt-0.5 h-5 w-5 rounded-sm inline-flex items-center justify-center hover:bg-accent shrink-0"
                        >
                            {isCollapsed ? (
                                <ChevronRight className="h-3.5 w-3.5 text-emerald-500" />
                            ) : (
                                <ChevronDown className="h-3.5 w-3.5 text-emerald-500" />
                            )}
                        </button>
                    ) : (
                        <span className="mt-1 h-5 w-5 inline-flex items-center justify-center shrink-0">
                            <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                        </span>
                    )}

                    <div className="min-w-0">
                        <p
                            className={`truncate text-sm leading-5 ${
                                hasChildren
                                    ? 'text-foreground font-medium'
                                    : 'text-muted-foreground'
                            }`}
                            title={node.name}
                        >
                            {node.name}
                        </p>
                        <p className="text-xs text-muted-foreground leading-4">
                            {getSkillTypeLabel(node.skillTypeId)}
                        </p>
                    </div>
                </div>

                <div
                    className={`flex items-center gap-1 ${
                        showActionsAlways
                            ? 'opacity-100'
                            : 'opacity-0 group-hover:opacity-100'
                    } transition-opacity shrink-0`}
                >
                    {showView && (
                        <button
                            type="button"
                            className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
                            onClick={() => onView(node.id)}
                        >
                            <Eye className="h-4 w-4" />
                        </button>
                    )}
                    {canManage && (
                        <>
                            <button
                                type="button"
                                className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
                                onClick={() => onCreateChild(node.id)}
                            >
                                <Plus className="h-4 w-4" />
                            </button>
                            <SkillFrameworkActions
                                isRoot={isRoot}
                                isPublished={isPublished}
                                isPublishing={isPublishing}
                                onPublish={onPublish}
                                onEdit={onEdit}
                                isDuplicating={isDuplicating}
                                onDuplicate={onDuplicate}
                                isDeleting={isDeleting}
                                onDelete={onDelete}
                                nodeId={node.id}
                            />
                        </>
                    )}
                </div>
            </div>

            {hasChildren && !isCollapsed && (
                <div>
                    {node.children!.map((child) => (
                        <HierarchyNodeItem
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            isRoot={false}
                            getSkillTypeLabel={getSkillTypeLabel}
                            collapsedMap={collapsedMap}
                            onToggle={onToggle}
                            onView={onView}
                            isStudent={isStudent}
                            canManage={canManage}
                            isPublished={isPublished}
                            isPublishing={isPublishing}
                            onPublish={onPublish}
                            onCreateChild={onCreateChild}
                            onEdit={onEdit}
                            isDuplicating={isDuplicating}
                            onDuplicate={onDuplicate}
                            isDeleting={isDeleting}
                            onDelete={onDelete}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const SkillFrameworkHierarchyDrawer = ({
    treeData,
    nodeStack,
    getSkillTypeLabel,
    collapsedMap,
    onToggle,
    onView,
    isStudent,
    canManage,
    height,
    isPublished,
    isPublishing,
    onPublish,
    onCreateChild,
    onEdit,
    isDuplicating,
    onDuplicate,
    isDeleting,
    onDelete,
    rootId,
}: SkillFrameworkHierarchyDrawerProps) => {
    const headerNode = nodeStack[0] ?? treeData ?? null;
    const headerDescription = normalizeSkillDescription(headerNode?.description);
    const children = headerNode?.children ?? [];
    if (!headerNode) {
        return (
            <aside
                className="border-l bg-slate-200 dark:bg-slate-800"
                style={{ height: typeof height === 'number' ? `${height}px` : height }}
            />
        );
    }

    return (
        <aside
            className="border-l bg-slate-200 dark:bg-slate-800 overflow-y-auto"
            style={{ height: typeof height === 'number' ? `${height}px` : height }}
        >
            <div className="sticky top-0 z-20 bg-slate-200 dark:bg-slate-800 border-b border-slate-300 dark:border-slate-700 px-3 pt-8 pb-3 flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                    <h3 className="text-base font-medium truncate">
                        {headerNode.name}
                    </h3>
                    {headerDescription && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                            {headerDescription}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {(isStudent || canManage) && (
                        <button
                            type="button"
                            className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
                            onClick={() => onView(headerNode.id)}
                        >
                            <Eye className="h-4 w-4" />
                        </button>
                    )}
                    {canManage && (
                        <>
                            <button
                                type="button"
                                className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
                                onClick={() => onCreateChild(headerNode.id)}
                            >
                                <Plus className="h-4 w-4" />
                            </button>
                            <SkillFrameworkActions
                                isRoot={String(headerNode.id) === String(rootId)}
                                isPublished={isPublished}
                                isPublishing={isPublishing}
                                onPublish={onPublish}
                                onEdit={onEdit}
                                isDuplicating={isDuplicating}
                                onDuplicate={onDuplicate}
                                isDeleting={isDeleting}
                                onDelete={onDelete}
                                nodeId={String(headerNode.id)}
                            />
                        </>
                    )}
                </div>
            </div>

            <div className="space-y-0.5 px-3 pb-3">
                {children.map((child) => (
                    <HierarchyNodeItem
                        key={child.id}
                        node={child}
                        depth={0}
                        isRoot={false}
                        getSkillTypeLabel={getSkillTypeLabel}
                        collapsedMap={collapsedMap}
                        onToggle={onToggle}
                        onView={onView}
                        isStudent={isStudent}
                        canManage={canManage}
                        isPublished={isPublished}
                        isPublishing={isPublishing}
                        onPublish={onPublish}
                        onCreateChild={onCreateChild}
                        onEdit={onEdit}
                        isDuplicating={isDuplicating}
                        onDuplicate={onDuplicate}
                        isDeleting={isDeleting}
                        onDelete={onDelete}
                    />
                ))}
            </div>
        </aside>
    );
};

export default SkillFrameworkHierarchyDrawer;
